import { DollarSign, FileText, History } from 'lucide-react';
import { useMemo } from 'react';

import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { StatusBadge } from '@/components/ui/status-badge';
import type { PricingPolicyQuery } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useTranslation } from 'react-i18next';

import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel.tsx';
import { PRICING_POLICY_QUERY } from './pricing-policies.gql.tsx';
import { PricingPolicyRateDetails } from './pricing-policy-rate-details';

type PricingPolicyDetailsData = NonNullable<
	PricingPolicyQuery['pricingPolicy']
>;

interface PricingPolicyDetailsProps {
	pricingPolicyId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	// Header props for modal display
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const PricingPolicyEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<DollarSign className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const PricingPolicyDetailsHeader = ({
	policy,
	className,
	...actions
}: {
	policy: Pick<PricingPolicyDetailsData, 'pricingPolicyId' | 'name' | 'isActive'>;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tP } = useTranslation('pricingPolicies');
	const entityTypeName = policy.isActive
		? tP('details.activePolicy')
		: tP('details.inactivePolicy');

	return (
		<EntityDetailsHeader
			icon={<PricingPolicyEntityIcon />}
			name={policy.name}
			statusBadge={<StatusBadge isActive={policy.isActive} />}
			entityTypeName={entityTypeName}
			entityId={actions.entityId ?? policy.pricingPolicyId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

const formatCurrency = (
	value: string | number | null | undefined,
	t?: (key: string) => string
) => {
	if (value == null) {
		return t ? t('details.notSet') : 'Not set';
	}

	const numericValue = typeof value === 'number' ? value : Number(value);

	if (Number.isNaN(numericValue)) {
		return t ? t('details.notSet') : 'Not set';
	}

	return new Intl.NumberFormat(undefined, {
		style: 'currency',
		currency: 'EUR'
	}).format(numericValue);
};

const formatDuration = (
	value: number | null | undefined,
	t?: (key: string) => string
) => {
	if (value == null) {
		return t ? t('details.notSet') : 'Not set';
	}

	const hours = Math.floor(value / 60);
	const minutes = value % 60;

	if (hours > 0 && minutes > 0) {
		return `${hours}h ${minutes}m`;
	}

	if (hours > 0) {
		return `${hours}h`;
	}

	return `${minutes}m`;
};

export function PricingPolicyDetails({
	pricingPolicyId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: PricingPolicyDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tP } = useTranslation('pricingPolicies');

	const { data, isLoading, isError } = useOne<PricingPolicyDetailsData>({
		resource: 'pricingPolicies',
		id: pricingPolicyId,
		meta: {
			gqlQuery: PRICING_POLICY_QUERY,
			dataPath: 'pricingPolicy'
		}
	});

	// For details pages, show loading immediately to prevent flash during navigation
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100, // Shorter delay for details pages
		showInitialLoading: true // Show loading immediately during navigation
	});

	const policy = data?.data;

	const limitSummary = useMemo(() => {
		if (!policy?.limit) {
			return tP('details.notSet');
		}

		const { minAmount, maxAmount, minStayMinutes, maxStayMinutes } =
			policy.limit;

		const amountParts: string[] = [];
		if (minAmount != null) {
			amountParts.push(
				`${tP('details.minAmount')} ${formatCurrency(minAmount, tP)}`
			);
		}
		if (maxAmount != null) {
			amountParts.push(
				`${tP('details.maxAmount')} ${formatCurrency(maxAmount, tP)}`
			);
		}

		const stayParts: string[] = [];
		if (minStayMinutes != null) {
			stayParts.push(
				`${tP('details.minStay')} ${formatDuration(minStayMinutes, tP)}`
			);
		}
		if (maxStayMinutes != null) {
			stayParts.push(
				`${tP('details.maxStay')} ${formatDuration(maxStayMinutes, tP)}`
			);
		}

		const amountSummary = amountParts.join(' • ');
		const staySummary = stayParts.join(' • ');

		if (amountSummary && staySummary) {
			return `${amountSummary} • ${staySummary}`;
		}

		return amountSummary || staySummary || tP('details.notSet');
	}, [policy?.limit, tP]);

	// Show skeleton during loading
	if (shouldShowLoading) {
		return (
			<div className="space-y-6">
				<div className="space-y-4">
					<div className="w-48 h-6 rounded bg-muted animate-pulse" />
					<div className="w-32 h-6 rounded bg-muted animate-pulse" />
					<div className="w-24 h-6 rounded bg-muted animate-pulse" />
					<div className="h-6 rounded w-36 bg-muted animate-pulse" />
					<div className="w-40 h-6 rounded bg-muted animate-pulse" />
				</div>
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
			</div>
		);
	}

	// Only show error when not loading
	if (!isLoading && (isError || !policy)) {
		return (
			<div className="py-8 text-base text-center text-muted-foreground">
				{tP('details.loadError')}
			</div>
		);
	}

	// If we don't have data yet, don't render anything (let loading states handle it)
	if (!policy) {
		return null;
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<PricingPolicyDetailsHeader
					policy={policy}
					entityId={pricingPolicyId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

			<Tabs
				defaultValue={activeTab !== undefined ? undefined : "details"}
				value={activeTab}
				onValueChange={onTabChange}
				className="w-full flex-1"
			>
			{!hideTabsList && (
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>
			)}

			<TabsContent value="details" className="space-y-6 text-base mt-6">
				<DetailSection>
					<DetailFieldsGrid>
						<DetailField
							label={tP('details.priority')}
							value={
								policy.priority?.toString() ?? tP('details.notSet')
							}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.assignments')}>
					<DetailFieldsGrid>
						<EntityDetailField
							label={tP('details.scenario')}
							value={policy.scenario?.name}
							entityId={policy.scenario?.scenarioId}
							entityType="scenario"
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.rates')}>
					<DetailFieldsGrid>
						<DetailField
							label={tP('details.primaryRate')}
							value={<PricingPolicyRateDetails rate={policy.rate} />}
							className="col-span-2"
						/>
						<DetailField
							label={tP('details.overstayRate')}
							value={<PricingPolicyRateDetails rate={policy.overstayRate} />}
							className="col-span-2"
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.billing_limits')}>
					<DetailFieldsGrid>
						<DetailField
							label={tP('details.billingCycle')}
							value={
								policy.billingCycle?.name ?? tP('details.notSet')
							}
						/>
						<DetailField
							label={tP('details.limitSummary')}
							value={limitSummary}
						/>
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab
					data={{
						activatedAt: policy.activatedAt,
						deactivatedAt: policy.deactivatedAt
					}}
				/>
			</TabsContent>
		</Tabs>
		</div>
	);
}
